/* oxlint-disable max-lines -- Event queries share one query key factory and invalidation root. */
import type { HomepageUpcomingEvent } from "@circle-react/components/HomePageV2/types";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import {
  homePageUpcomingEventsPath,
  internalApi,
} from "@circle-react/helpers/urlHelpers";
import { workflows } from "@circle-react/helpers/urlHelpers/internalApi/workflows";
import { createQueryKeyFactory } from "@circle-react/lib/queries/createQueryKeyFactory";
import { filterKitOptionInvalidations } from "@circle-react/lib/queries/filterKitOptions";
import { postInvalidations } from "@circle-react/lib/queries/posts";
import { spaceKey } from "@circle-react/lib/queries/spaces";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import type { CommunityMemberCharge } from "@circle-react/types/CommunityMemberCharge";
import type {
  Event,
  EventAttendee,
  EventAttendeeRsvpStatus,
} from "@circle-react/types/Event";
import {
  keepPreviousData,
  infiniteQueryOptions,
  queryOptions,
} from "react-query";

// Event query factory. Root string matches CircleAi::Intent::RESOURCES wire name.
// All keys share the ["event", ...] prefix so one root invalidation covers every
// list, detail, calendar, attendee, and homepage cache entry.
export const eventKey = createQueryKeyFactory("event");
export const DEFAULT_EVENTS_PER_PAGE = 20;
export const CALENDAR_EVENTS_PER_PAGE = 120;
export const EVENTS_FILTER_PER_PAGE = 100;
export const EVENT_TRANSACTIONS_PER_PAGE = 5;
const paramsKey = (params: Record<string, unknown> = {}) =>
  JSON.stringify(
    Object.entries(params).sort(([left], [right]) => left.localeCompare(right)),
  );
const allEventsListKey = (
  params: Record<string, unknown> = {},
  isCalendarView = false,
) =>
  eventKey.key(
    "list",
    paramsKey(params),
    isCalendarView ? "calendar" : "default",
  );
interface EventListPage {
  count: number;
  has_next_page: boolean;
  page: number;
  per_page: number;
  records: Event[];
}
interface EventFilterRecord {
  id: number;
  name: string;
  start_date: string;
  upcoming_event: boolean;
}
interface EventFilterListPage {
  count: number;
  has_next_page: boolean;
  page: number;
  per_page: number;
  records: EventFilterRecord[];
}
interface AllEventsListParams {
  isCalendarView?: boolean;
  params?: Record<string, unknown>;
  perPage?: number;
}
interface EventAttendeesParams {
  page?: number;
  perPage?: number;
  role?: string;
  rsvpStatus?: EventAttendeeRsvpStatus;
  slug?: string;
  spaceId?: number;
}
export type EventPeopleV4LiveAttendance =
  | "attended"
  | "no_show"
  | "didnt_attend";

export interface EventPeopleV4Filters {
  addedOnFrom?: string;
  addedOnTo?: string;
  email?: string;
  liveAttendance?: EventPeopleV4LiveAttendance;
  name?: string;
  role?: "participant" | "cohost";
  rsvpStatus?: EventAttendeeRsvpStatus;
}
export interface EventPeopleV4Params
  extends Omit<EventAttendeesParams, "role">, EventPeopleV4Filters {
  sortBy?: "name" | "rsvp_status" | "email" | "added_on";
  sortDirection?: "asc" | "desc";
  viewerTimeZone?: string;
}
export interface EventAttendeesResponse {
  count: number;
  has_next_page: boolean;
  page: number;
  page_count: number;
  records: EventAttendeeRecord[];
  total_confirmed_record_count: number;
}
export interface EventPeopleV4Summary {
  invited: number;
  no: number;
  yes: number;
}
export interface EventPeopleV4Record extends Omit<
  EventAttendeeRecord,
  "role" | "email"
> {
  email?: string;
  live_attendance: EventPeopleV4LiveAttendance | null;
  role: "participant" | "cohost";
  spot_status: "confirmed";
}
export interface EventPeopleV4Response {
  added_on_time_zone: string;
  count: number;
  has_next_page: boolean;
  live_attendance_available: boolean;
  live_attendance_sortable: boolean;
  page: number;
  page_count: number;
  per_page: number;
  records: EventPeopleV4Record[];
  rsvp_summary: EventPeopleV4Summary;
}
type EventAttendeeRecord = Omit<
  EventAttendee,
  "access_type" | "avatar_url" | "email" | "added_on" | "spot_status"
> & {
  access_type: string;
  added_on: string;
  avatar_url: string;
  charge?: CommunityMemberCharge;
  email: string;
  spot_status: NonNullable<EventAttendee["spot_status"]>;
};
export type EventTransactionRecord = EventAttendeeRecord & {
  access_type: "invited" | "rsvp";
  complimentary_access: boolean;
};
export interface EventTransactionsResponse {
  count: number;
  has_next_page: boolean;
  page: number;
  per_page: number;
  records: EventTransactionRecord[];
}
type EventTransactionsParams = Omit<EventAttendeesParams, "perPage" | "role">;
export const eventQueries = {
  filterList: () =>
    infiniteQueryOptions({
      initialPageParam: 1,
      queryKey: eventKey.key("list", "filter"),
      queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
        reactQueryGet<EventFilterListPage>(
          workflows.events.index({
            params: {
              page: pageParam,
              per_page: EVENTS_FILTER_PER_PAGE,
            },
          }),
        ),
      getNextPageParam: (lastPage: EventFilterListPage) =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
    }),

  allEventsList: ({
    params = {},
    isCalendarView = false,
    perPage = isCalendarView
      ? CALENDAR_EVENTS_PER_PAGE
      : DEFAULT_EVENTS_PER_PAGE,
  }: AllEventsListParams = {}) =>
    infiniteQueryOptions({
      initialPageParam: 1,
      queryKey: allEventsListKey(params, isCalendarView),
      queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
        reactQueryGet<EventListPage>(
          internalApi.events.allEvents({
            params: {
              page: pageParam,
              per_page: perPage,
              ...params,
            },
          }),
        ),
      getNextPageParam: (lastPage: EventListPage) =>
        lastPage.has_next_page ? Number(lastPage.page) + 1 : undefined,
    }),

  detailBySlug: (spaceId: number, slug: string) =>
    queryOptions({
      queryKey: eventKey.key("detail", spaceId, slug),
      queryFn: () =>
        reactQueryGet<Event>(internalApi.events.show({ slug, spaceId })),
    }),

  detailById: (eventId: string | number) =>
    queryOptions({
      queryKey: eventKey.key("detail", eventId),
      queryFn: () =>
        reactQueryGet<Event>(workflows.events.show({ id: String(eventId) })),
    }),

  attendees: ({
    spaceId,
    slug,
    page = 1,
    role,
    perPage,
    rsvpStatus,
  }: EventAttendeesParams) => {
    const normalizedSpaceId = spaceId ?? 0;
    const normalizedSlug = slug ?? "";
    const normalizedPerPage = perPage ?? 10;
    return queryOptions({
      queryKey: eventKey.key(
        "attendees",
        normalizedSpaceId,
        normalizedSlug,
        role ?? "",
        page,
        normalizedPerPage,
        rsvpStatus ?? "",
      ),
      queryFn: () =>
        reactQueryGet<EventAttendeesResponse>(
          internalApi.eventAttendees.index({
            spaceId: normalizedSpaceId,
            slug: normalizedSlug,
            params: {
              page,
              role,
              per_page: normalizedPerPage,
              ...(rsvpStatus ? { rsvp_status: rsvpStatus } : {}),
            },
          }),
        ),
    });
  },

  attendeesInfinite: ({
    spaceId,
    slug,
    role,
    rsvpStatus,
  }: Omit<EventAttendeesParams, "page" | "perPage">) => {
    const normalizedSpaceId = spaceId ?? 0;
    const normalizedSlug = slug ?? "";

    return infiniteQueryOptions({
      initialPageParam: 1,
      queryKey: eventKey.key(
        "attendees",
        normalizedSpaceId,
        normalizedSlug,
        "infinite",
        role ?? "",
        rsvpStatus ?? "",
      ),
      queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
        reactQueryGet<EventAttendeesResponse>(
          internalApi.eventAttendees.index({
            spaceId: normalizedSpaceId,
            slug: normalizedSlug,
            params: {
              page: pageParam,
              role,
              per_page: 10,
              ...(rsvpStatus ? { rsvp_status: rsvpStatus } : {}),
            },
          }),
        ),
      getNextPageParam: (lastPage: EventAttendeesResponse) =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
      staleTime: 0,
    });
  },

  peopleV4: ({
    spaceId,
    slug,
    page = 1,
    perPage,
    rsvpStatus,
    liveAttendance,
    name,
    email,
    role,
    addedOnFrom,
    addedOnTo,
    sortBy,
    sortDirection,
    viewerTimeZone,
  }: EventPeopleV4Params) => {
    const normalizedSpaceId = spaceId ?? 0;
    const normalizedSlug = slug ?? "";
    const normalizedPerPage = perPage ?? 10;
    const filter = {
      ...(name?.trim() ? { name: name.trim() } : {}),
      ...(rsvpStatus ? { rsvp_status: rsvpStatus } : {}),
      ...(liveAttendance ? { live_attendance: liveAttendance } : {}),
      ...(email?.trim() ? { email: email.trim() } : {}),
      ...(role ? { role } : {}),
      ...(addedOnFrom ? { added_on_from: addedOnFrom } : {}),
      ...(addedOnTo ? { added_on_to: addedOnTo } : {}),
    };

    return queryOptions<EventPeopleV4Response>({
      placeholderData: keepPreviousData,
      queryKey: eventKey.key(
        "people-v4",
        normalizedSpaceId,
        normalizedSlug,
        name?.trim() ?? "",
        rsvpStatus ?? "",
        liveAttendance ?? "",
        email?.trim() ?? "",
        role ?? "",
        addedOnFrom ?? "",
        addedOnTo ?? "",
        sortBy ?? "added_on",
        sortDirection ?? "desc",
        page,
        normalizedPerPage,
        viewerTimeZone?.trim() || "UTC",
      ),
      queryFn: () =>
        reactQueryGet<EventPeopleV4Response>(
          internalApi.eventAttendees.index({
            spaceId: normalizedSpaceId,
            slug: normalizedSlug,
            params: {
              page,
              people_v4: true,
              per_page: normalizedPerPage,
              ...(Object.keys(filter).length ? { filter } : {}),
              ...(sortBy ? { sort_by: sortBy } : {}),
              ...(sortDirection ? { sort_direction: sortDirection } : {}),
            },
          }),
        ),
    });
  },

  transactions: ({ spaceId, slug, page = 1 }: EventTransactionsParams) => {
    const normalizedSpaceId = spaceId ?? 0;
    const normalizedSlug = slug ?? "";

    return queryOptions<EventTransactionsResponse>({
      queryKey: eventKey.key(
        "transactions",
        normalizedSpaceId,
        normalizedSlug,
        page,
        EVENT_TRANSACTIONS_PER_PAGE,
      ),
      queryFn: () =>
        reactQueryGet<EventTransactionsResponse>(
          internalApi.eventAttendees.index({
            spaceId: normalizedSpaceId,
            slug: normalizedSlug,
            params: {
              page,
              payment_transactions_only: true,
              per_page: EVENT_TRANSACTIONS_PER_PAGE,
            },
          }),
        ),
      placeholderData: keepPreviousData,
    });
  },

  homepageUpcoming: () =>
    queryOptions({
      queryKey: eventKey.key("homepage-upcoming"),
      queryFn: async () => {
        const { events } = await reactQueryGet<{
          events: HomepageUpcomingEvent[];
        }>(homePageUpcomingEventsPath());
        return events;
      },
    }),
};

export const eventInvalidations = {
  all: () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: eventKey.root() }),
      eventInvalidations.filterOptions(),
    ]),

  filterOptions: () => filterKitOptionInvalidations.forFilter("events"),

  detail: (id: number | string) =>
    queryClient.invalidateQueries({ queryKey: eventKey.key("detail", id) }),

  peopleV4: (spaceId: number, slug: string) =>
    queryClient.invalidateQueries({
      queryKey: eventKey.key("people-v4", spaceId, slug),
    }),

  attendeesForEvent: (spaceId: number, slug: string) =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: eventKey.key("attendees", spaceId, slug),
      }),
      eventInvalidations.peopleV4(spaceId, slug),
    ]),

  attendeesAndTransactionsForEvent: (spaceId: number, slug: string) =>
    Promise.all([
      eventInvalidations.attendeesForEvent(spaceId, slug),
      queryClient.invalidateQueries({
        queryKey: eventKey.key("transactions", spaceId, slug),
      }),
    ]),

  postBackedSurfaces: () =>
    Promise.all([
      postInvalidations.allPostDetails(),
      postInvalidations.feedPosts(),
      postInvalidations.allSpacePosts(),
      queryClient.invalidateQueries({
        queryKey: spaceKey.key("detail"),
        refetchType: "all",
        predicate: query => typeof query.queryKey[2] === "number",
      }),
    ]),
};
